import { useId, useState, type KeyboardEvent, type ReactNode } from "react";
import { Icon } from "./ui";

/**
 * Form fields to the familiar admin's shapes on the club's tokens: a label above, help
 * below, the error under the field in place of the help, the field itself
 * 40px at radius-action with the ink focus ring.
 */

interface FieldProps {
  label: string;
  help?: ReactNode;
  error?: string;
  children: (id: string, describedBy: string | undefined) => ReactNode;
  suffix?: ReactNode;
}

export function Field({ label, help, error, children, suffix }: FieldProps) {
  const id = useId();
  const helpId = `${id}-help`;
  const errId = `${id}-err`;
  return (
    <div className={`field${error ? " field--error" : ""}`}>
      <label htmlFor={id}>{label}</label>
      <div className="field__control">
        {children(id, error ? errId : help ? helpId : undefined)}
        {suffix && <span className="field__suffix">{suffix}</span>}
      </div>
      {error ? (
        <p className="field__error" id={errId}>
          <Icon.alert />
          {error}
        </p>
      ) : help ? (
        <p className="field__help" id={helpId}>
          {help}
        </p>
      ) : null}
    </div>
  );
}

export function TextField({ label, value, onChange, help, error, placeholder, maxLength, multiline, rows, suffix, mono, secret }: { label: string; value: string; onChange: (v: string) => void; help?: ReactNode; error?: string; placeholder?: string; maxLength?: number; multiline?: boolean; rows?: number; suffix?: ReactNode; mono?: boolean; /** Typed as dots, like a password, and never offered by the browser's autofill. */ secret?: boolean }) {
  return (
    <Field label={label} help={help ?? (maxLength ? <span className="field__count">{value.length}/{maxLength}</span> : undefined)} error={error} suffix={suffix}>
      {(id, describedBy) =>
        multiline ? (
          <textarea id={id} value={value} rows={rows ?? 4} placeholder={placeholder} maxLength={maxLength} aria-invalid={!!error} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)} className={mono ? "is-mono" : undefined} />
        ) : (
          <input id={id} type={secret ? "password" : "text"} autoComplete={secret ? "new-password" : undefined} spellCheck={secret ? false : undefined} value={value} placeholder={placeholder} maxLength={maxLength} aria-invalid={!!error} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)} className={mono ? "is-mono" : undefined} />
        )
      }
    </Field>
  );
}

/** A whole number. The draft keeps what was typed (a string while it is not a number) so the store's own message can come back. */
export function NumberField({ label, value, onChange, help, error, min, max, suffix, width }: { label: string; value: number | string; onChange: (v: number | string) => void; help?: ReactNode; error?: string; min?: number; max?: number; suffix?: string; width?: number }) {
  return (
    <Field label={label} help={help} error={error} suffix={suffix}>
      {(id, describedBy) => (
        <input
          id={id}
          type="number"
          inputMode="numeric"
          value={value}
          min={min}
          max={max}
          step={1}
          style={width ? { width } : undefined}
          aria-invalid={!!error}
          aria-describedby={describedBy}
          onChange={(e) => {
            const raw = e.target.value;
            onChange(/^-?\d+$/.test(raw) ? Number(raw) : raw);
          }}
        />
      )}
    </Field>
  );
}

export function SelectField({ label, value, onChange, options, help, error }: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; help?: ReactNode; error?: string }) {
  return (
    <Field label={label} help={help} error={error}>
      {(id, describedBy) => (
        <select id={id} className="select select--field" value={value} aria-invalid={!!error} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

export function CheckboxField({ label, checked, onChange, help }: { label: string; checked: boolean; onChange: (v: boolean) => void; help?: ReactNode }) {
  const id = useId();
  return (
    <div className="check">
      <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <label htmlFor={id}>
        {label}
        {help && <small>{help}</small>}
      </label>
    </div>
  );
}

/** An ordered list of short names: type one, press Enter or Add; remove with its ×; move with the arrows. */
export function TagsField({ label, value, onChange, help, error, placeholder, max, maxLength }: { label: string; value: string[]; onChange: (v: string[]) => void; help?: ReactNode; error?: string; placeholder?: string; max?: number; maxLength?: number }) {
  const [text, setText] = useState("");
  const full = max !== undefined && value.length >= max;
  const add = () => {
    const name = text.replace(/\s+/g, " ").trim();
    if (!name || full) return;
    if (value.some((v) => v.toLowerCase() === name.toLowerCase())) {
      setText("");
      return;
    }
    onChange([...value, name]);
    setText("");
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      add();
    } else if (e.key === "Backspace" && text === "" && value.length) {
      onChange(value.slice(0, -1));
    }
  };
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
  };
  return (
    <Field label={label} help={help} error={error}>
      {(id, describedBy) => (
        <div className="tags">
          <div className="tags__add">
            <input id={id} type="text" value={text} placeholder={full ? `Up to ${max}` : placeholder} maxLength={maxLength} disabled={full} aria-invalid={!!error} aria-describedby={describedBy} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} />
            <button type="button" className="btn" onClick={add} disabled={full || !text.trim()}>
              Add
            </button>
          </div>
          {value.length > 0 && (
            <ul className="tags__list">
              {value.map((v, i) => (
                <li key={v} className="tag">
                  <span className="tag__text">{v}</span>
                  <span className="tag__tools">
                    <button type="button" aria-label={`Move ${v} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                      <Icon.chevL />
                    </button>
                    <button type="button" aria-label={`Move ${v} down`} disabled={i === value.length - 1} onClick={() => move(i, 1)}>
                      <Icon.chevR />
                    </button>
                    <button type="button" aria-label={`Remove ${v}`} onClick={() => onChange(value.filter((_, k) => k !== i))}>
                      <Icon.close />
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Field>
  );
}

/** A calendar date, kept as YYYY-MM-DD. */
export function DateField({ label, value, onChange, help, error, min, max }: { label: string; value: string; onChange: (v: string) => void; help?: ReactNode; error?: string; min?: string; max?: string }) {
  return (
    <Field label={label} help={help} error={error}>
      {(id, describedBy) => <input id={id} type="date" value={value} min={min} max={max} aria-invalid={!!error} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value)} />}
    </Field>
  );
}

/** A time of day, kept as HH:MM. */
export function TimeField({ label, value, onChange, help, error }: { label: string; value: string; onChange: (v: string) => void; help?: ReactNode; error?: string }) {
  return (
    <Field label={label} help={help} error={error}>
      {(id, describedBy) => <input id={id} type="time" step={60} value={value} aria-invalid={!!error} aria-describedby={describedBy} onChange={(e) => onChange(e.target.value.slice(0, 5))} />}
    </Field>
  );
}

/** A set of choices, any number of which may be on; the value keeps the options' order. */
export function CheckGroupField({ label, value, options, onChange, help, error, inline }: { label: string; value: string[]; options: { value: string; label: string; help?: string }[]; onChange: (v: string[]) => void; help?: ReactNode; error?: string; inline?: boolean }) {
  const id = useId();
  const toggle = (v: string, on: boolean) => {
    const set = new Set(value);
    if (on) set.add(v);
    else set.delete(v);
    onChange(options.map((o) => o.value).filter((o) => set.has(o)));
  };
  return (
    <fieldset className={`field field--group${error ? " field--error" : ""}`} aria-describedby={error ? `${id}-err` : help ? `${id}-help` : undefined}>
      <legend>{label}</legend>
      <div className={`checks${inline ? " checks--inline" : ""}`}>
        {options.map((o) => (
          <CheckboxField key={o.value} label={o.label} help={o.help} checked={value.includes(o.value)} onChange={(on) => toggle(o.value, on)} />
        ))}
      </div>
      {error ? (
        <p className="field__error" id={`${id}-err`}>
          <Icon.alert />
          {error}
        </p>
      ) : help ? (
        <p className="field__help" id={`${id}-help`}>
          {help}
        </p>
      ) : null}
    </fieldset>
  );
}
